import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs/Rx';
import 'rxjs/add/operator/mergeMap';
import * as moment from 'moment';
import { BehaviorSubject } from 'rxjs';

import { environment } from '../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { FormField } from '../forms/form-field.model';
import { FormSection } from '../forms/form-section.model';
import { Template } from '../templates/template.model';
import { User } from '../users/user.model';
import { Workflow } from '../workflows/workflow.model';
import { QCTVWorkflow } from '../workflows/qc-tv-workflow';
import { Anomalies } from '../workflows/anomalies';
import { FormatCheckWorkflow } from '../workflows/format-check-workflow';
import { FormatCheckTemplate } from '../templates/format-check-template';
import { Jr3MediaWorkflow } from '../workflows/jr3-media-workflow';

interface Profile {
  templates: Template[];
  workflows: Workflow[];
  favoriteTemplateIds: number[];
  canCreateTemplates: boolean;
  canAccessQC: boolean;
  onlyQC: boolean;
  canAccessV4: boolean;
  aocEnabled: boolean;
  settings: [{setting_name: string, setting_value: any}];
}

@Injectable()
export class UsersService {

  apiHost = environment.apiHost;
  private profileSubject: BehaviorSubject<Profile> = new BehaviorSubject<Profile>(null);
  public profile$ = this.profileSubject.asObservable();

  get profile(): Observable<Profile> {
    const storedProfile = localStorage.getItem('userProfile');
    if (storedProfile != null) {
      const profile = JSON.parse(storedProfile) as Profile;
      this.profileSubject.next(profile);
      return Observable.of(profile);
    } else {
      return this.getProfile();
    }
  }

  get aocEnabled(): boolean {
    let storedProfileJson = localStorage.getItem('userProfile');
    let storedProfile = JSON.parse(storedProfileJson) as Profile;
    return storedProfile.aocEnabled;
  }

  constructor(
    private http: HttpClient,
    private authService: AuthService) {}

  create(email: string, id: string, first?: string, last?: string): Observable<boolean> {
    const payload = {
      user_id: id,
      email: email,
      first_name: first,
      last_name: last
    };
    return this.http.post(this.apiHost + '/users', payload).map(
      (data: any) => {
        console.log('User created: ' + email);
        return data;
      },
      (errors) => console.log('User could not be created: ' + email)
    );
  }

  search(email: string, limit: number = 8): Observable<any> {
    const query = '?query=' + encodeURIComponent(email) + '&limit=' + limit;
    return this.http.get(this.apiHost + '/users/search' + query).map(
      (data: any) => {
        const users = data.map((d) => User.fromRecord(d));
        return users;
      }
    ).catch((error) => {
        console.log('Search users error:'); console.log(error);
        if (error.status === 401) {
          this.authService.signIn();
        }
        return Observable.throw(new Error(error.status));
      }
    );
  }

  getProfile(): Observable<Profile> {
    return this.http.get(this.apiHost + '/users/profile').map(
      (data: any) => {
        const profile = this.parseProfile(data);
        localStorage.setItem('userProfile', JSON.stringify(profile));
        this.profileSubject.next(profile);
        return profile;
      }
    ).catch((error) => {
        console.log('Get user profile error:'); console.log(error);
        if (error.status === 401) {
          this.authService.signIn();
        }
        return Observable.throw(new Error(error.status));
      }
    );
  }

  updateProfile(profile): Observable<Profile> {
    const payload = {settings: {settings: profile.settings}};
    return this.http.put(this.apiHost + '/users/profile', payload).map(
      (data: any) => {
        localStorage.setItem('userProfile', JSON.stringify(profile));
        this.profileSubject.next(profile);
        return profile;
      }
    ).catch((error) => {
        console.log('Update user profile error:'); console.log(error);
        if (error.status === 401) {
          this.authService.signIn();
        }
        return Observable.throw(new Error(error.status));
      }
    );
  }

  updateFavorites(favoriteTemplateIds, templates) {
    return this.profile.flatMap(
      (profile) => {

        profile.favoriteTemplateIds = favoriteTemplateIds;
        profile.templates = templates;

        // Update the settings object
        const joinedIds = profile.favoriteTemplateIds.join(',');
        const setting = profile.settings.find((s) => s.setting_name === 'favorite_templates');
        if (setting != null) {
          setting.setting_value = joinedIds;
        } else {
          profile.settings.push({
            setting_name: 'favorite_templates',
            setting_value: joinedIds
          });
        }

        return this.updateProfile(profile);
      }
    );
  }

  updateProfileTemplates(templates) {
    return this.profile.subscribe(
      (profile) => {
        profile.templates = templates;
        localStorage.setItem('userProfile', JSON.stringify(profile));
      }
    );
  }

  parseProfile(data) {
    const templates = [];

    for (const f of data.favorite_templates) {
      const template = Template.fromRecord(f);
      template.isFavorite = true;
      const existing = templates.find((t) => t.id === template.id);
      if (existing != null) {
        existing.isFavorite = true;
      } else {
        templates.push(template);
      }
    }

    for (const r of data.recent_templates) {
      const template = Template.fromRecord(r);
      const existing = templates.find((t) => t.id === template.id);
      if (existing != null) {
        existing.isRecent = true;
      } else {
        template.isRecent = true;
        templates.push(template);
      }
    }

    const commonValues = this.parseCommonValues(data.nrg_terms);
    const languages = commonValues['Andouillette|Language Details|'];
    const dualLanguages = [];
    const restOfLanguages = [];

    for (const l in languages) {
      const dual = 'DUAL';
      const displayed = languages[l].displayed;
      const stored = languages[l].stored;
      if (displayed.indexOf(dual) > -1) {
        dualLanguages.push({
          displayed: displayed,
          stored: stored
        });
      } else {
        restOfLanguages.push({
          displayed: displayed,
          stored: stored
        });
      }
    }

    const combinedLangauges = restOfLanguages.concat(dualLanguages);

    //  do a find for popular languages
    let english,
        englishUK,
        french,
        italian,
        german,
        spanishCast,
        spanishLatin,
        brazilPort,
        japanese,
        korean;

    for (const lang in combinedLangauges) {
      const index = combinedLangauges[lang];
      if (index.displayed == 'ENGLISH (ENG)') {
        english = index;
      } else if (index.displayed == 'ENGLISH (UK) (EUK)') {
        englishUK = index;
      } else if (index.displayed == 'FRENCH (PARIS) (FRP)') {
        french = index;
      } else if (index.displayed == 'ITALIAN (ITA)') {
        italian = index;
      } else if (index.displayed == 'GERMAN (GER)') {
        german = index;
      } else if (index.displayed == 'SPANISH (CASTILIAN) (SPC)') {
        spanishCast = index;
      } else if (index.displayed == 'SPANISH (LATIN AMERICA) (LAS)') {
        spanishLatin = index;
      } else if (index.displayed == 'PORTUGUESE (BRAZIL) (PTB)') {
        brazilPort = index;
      } else if (index.displayed == 'JAPANESE (JPN)') {
        japanese = index;
      } else if (index.displayed == 'KOREAN (KOR)') {
        korean = index;
      }
    }

    if (korean != null) { combinedLangauges.unshift(korean); }
    if (japanese != null) { combinedLangauges.unshift(japanese); }
    if (brazilPort != null) { combinedLangauges.unshift(brazilPort); }
    if (spanishLatin != null) { combinedLangauges.unshift(spanishLatin); }
    if (spanishCast != null) { combinedLangauges.unshift(spanishCast); }
    if (german != null) { combinedLangauges.unshift(german); }
    if (italian != null) { combinedLangauges.unshift(italian); }
    if (french != null) { combinedLangauges.unshift(french); }
    if (englishUK != null) { combinedLangauges.unshift(englishUK); }
    if (english != null) { combinedLangauges.unshift(english); }
    commonValues['Andouillette|Language Details|'] = combinedLangauges;

    let canAccessQC = false;
    let onlyQC = true;
    let canAccessV4 = false;
    let workflows: Workflow[] = [];
    for (const d of data.workflows) {
      const workflow = this.parseWorkflow(d, commonValues);
      if (workflow.type == 'QC') {
        canAccessQC = true;
      } else {
        onlyQC = false;
      }
      if (workflow.id == 117) {
        canAccessV4 = true;
      }
      workflows.push(workflow);
    }
    // Replace database workflows with hardcoded ones for now
    for (const workflow of [QCTVWorkflow, FormatCheckWorkflow, Jr3MediaWorkflow]) {
      const existing = workflows.find((w) => w.id === workflow.id);
      if (existing != null) {
        const index = workflows.indexOf(existing);
        workflows[index] = this.parseWorkflow(workflow, commonValues);
      }
    }
    workflows = workflows.sort(
      (a, b) => a.name.localeCompare(b.name)
    );

    const settings = data.settings.settings;
    let favoriteTemplateIds = [];
    let canCreateTemplates = false;
    let aocEnabled = false;
    for (const setting of settings) {
      switch (setting.setting_name) {
      case 'favorite_templates':
        favoriteTemplateIds = setting.setting_value.split(',').map((v) => +v).filter((n) => !isNaN(n));
        break;
      case 'create_templates':
        canCreateTemplates = ('' + setting.setting_value === 'true');
        break;
      case 'aoc_enabled':
        aocEnabled = ('' + setting.setting_value === 'true');
        break;
      }
    }

    const profile = {
      templates: templates,
      workflows: workflows,
      favoriteTemplateIds: favoriteTemplateIds,
      canCreateTemplates: canCreateTemplates,
      canAccessQC: canAccessQC,
      onlyQC: (canAccessQC && onlyQC),
      canAccessV4: canAccessV4,
      aocEnabled: aocEnabled,
      settings: settings
    };
    return profile;
  }

  parseWorkflow(data, commonValues) {
    const workflow = new Workflow();
    workflow.id = data.id;
    workflow.name = data.wfName;
    workflow.description = data.wfDescription;
    workflow.wide = data.wide;
    workflow.type = data.wfType;
    workflow.header = data.wfData.header;
    if (data.wfData.validations != null) {
      const v = data.wfData.validations;
      workflow.validations = {atLeastOneRequired: v.at_least_one_required};
    }
    const sections = data.wfData.visible;
    if (sections != null) {
      workflow.sections = sections.map((s) => this.parseSection(s, commonValues));
    }
    const hiddenFields = data.wfData.hidden;
    if (hiddenFields != null) {
      workflow.hiddenFields = hiddenFields.map(
        (f) => this.parseField(f, commonValues)
      );
    }
    return workflow;
  }

  parseSection(data, commonValues) {
    const section = new FormSection();
    section.name = data.section_name;
    section.navigationName = data.navigation_name;
    section.columns = data.width;
    section.atLeastOneRequired = data.at_least_one_required;

    if (data.rows != null) {
      section.rows = data.rows.map((r) => r.map(
        (f) => this.parseField(f, commonValues))
      );
    }
    if (data.subsections != null) {
      section.subsections = data.subsections.map(
        (s) => this.parseSection(s, commonValues)
      );
    }
    return section;
  }

  parseField(data, commonValues) {
    const field = new FormField();
    field.add = data.options.add;
    field.id = data.id.trim();
    field.hint = data.hint;
    field.hints = data.hints;
    field.label = data.label;
    field.linkedPattern = data.options.linked_pattern;
    field.links = data.links;
    field.locked = (data.locked === true);
    field.max = data.options.max != null ? data.options.max : data.options.max_value;
    field.min = data.options.min != null ? data.options.min : data.options.min_value;
    field.path = data.options.path;
    field.pattern = data.options.pattern;
    field.placeholder = data.options.placeholder;
    field.required = data.options.required;
    field.rowId = data.row_id;
    field.sortBy = data.options.sort_by;
    field.type = data.type;
    field.columns = data.width;
    field.align = data.align;
    field.highlightValues = data.options.highlight_values;
    field.highlightPattern = data.options.highlight_pattern;
    field.highlightColor = data.options.highlight_color;
    field.highlightLinkedId = data.highlight_linked_id;
    field.disclaimers = data.options.disclaimers;
    field.csv = (data.options.csv === true);
    field.prefix = data.options.prefix;
    field.suffix = data.options.suffix;
    field.suffixes = data.options.suffixes;
    field.integer = (data.options.integer === true);
    field.positive = (data.options.positive === true);
    field.frames = data.options.frames;
    field.format = data.options.format;
    field.files = data.options.files;
    field.folders = data.options.folders;
    field.validValues = data.options.valid_values;
    field.dropdownWidth = data.options.dropdownWidth;
    field.draggable = data.options.draggable;
    const value = data.options.default;
    if (value != null &&
      value.stored != null && value.stored.trim() !== '' &&
      value.displayed != null && value.displayed.trim() !== '') {
      field.defaultValue = value;
    }
    if (data.type === 'nrg' && commonValues[data.options.path] != null) {
      field.values = commonValues[data.options.path];
      if (data.options.data != null) {
        for (const option of data.options.data) {
          const index = field.values.findIndex((x) => x.stored == option.stored);
          if (index != null) { field.values[index] = option; };
        }
      }
      field.type = 'autocomplete_select';
    } else if (data.options.data != null) {
      const values = data.options.data.filter((v) => {
        return v.stored != null && v.stored.trim() !== '' &&
        v.displayed != null && v.displayed.trim() !== '';
      });
      if (values.length > 0) {
        field.values = data.options.data;
      }
      field.blankOptionVisible = data.options.blankOptionVisible;
    }
    if (data.type === 'anomalies') {
      field.type = 'autocomplete_select';
      field.values = Anomalies.map((a) => {
        return {
          stored: a.name,
          displayed: a.name,
          categories: a.categories,
          description: a.description
        };
      });
    }
    if (data.fields != null) {
      field.fields = data.fields.map((f) => this.parseField(f, commonValues));
    } else if (data.options.fields != null ) {
      field.fields = data.options.fields.map((r) => r.map((f) => this.parseField(f, commonValues)));
    }
    return field;
  }

  parseCommonValues(data) {
    const values = {};
    for (const t of data) {
      const value = {
        displayed: t.term_text,
        stored: t.ext_term_id
      };
      if (value.displayed != null) {
        value.displayed = ('' + value.displayed).replace('â??', '-');
      }
      const path = t.hierarchy_path;
      if (values[path] == null) {values[path] = []; };
      values[path].push(value);
    }
    return values;
  }

}



// WEBPACK FOOTER //
// ./src/app/users/users.service.ts